// src/utils/inlineMediaUtils.ts
// Utility functions for handling inline media in descriptions
/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.86, "tu3": 3, "notes": "Cohesive set of media helpers; mixes pure URL logic with storage calls but each function stays small.", "tuVersion": "2.5"}
 * .help "Utilities for inline media in rich descriptions: URL type detection, file uploads to storage, and fetching or cleaning up media records."
 */
// Used by: RichDescriptionEditor, RichDescriptionDisplay, DescriptionModal

import { supabase } from '../lib/supabase';
import { InlineMediaItem } from '../types';

// ============================================
// URL VALIDATION
// ============================================

export const isValidUrl = (text: string): boolean => {
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

// ============================================
// MEDIA TYPE DETECTION
// ============================================

export const detectMediaTypeFromUrl = (url: string): 'image' | 'video' | 'document' | 'link' => {
  // Video platforms
  if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com')) {
    return 'video';
  }

  // Image extensions
  if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)) {
    return 'image';
  }

  // Document extensions (direct file URLs)
  if (url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|csv)$/i)) {
    return 'document';
  }

  // Google Workspace / Drive (docs, sheets, slides, file view)
  const lower = url.toLowerCase();
  if (
    lower.includes('docs.google.com') ||
    lower.includes('sheets.google.com') ||
    lower.includes('slides.google.com') ||
    lower.includes('drive.google.com')
  ) {
    if (
      lower.includes('/document') ||
      lower.includes('/spreadsheets') ||
      lower.includes('/presentation') ||
      lower.includes('/file/') ||
      lower.includes('/open?id=')
    ) {
      return 'document';
    }
    return 'link';
  }

  // Default to link
  return 'link';
};

// ============================================
// FILE UPLOAD
// ============================================

export interface UploadFileResult {
  publicUrl: string;
  filePath: string;
}

export const uploadFileToStorage = async (
  file: File,
  companyId: string,
): Promise<UploadFileResult> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const filePath = `${companyId}/${fileName}`;

  const { error } = await supabase.storage
    .from('shared-files')
    .upload(filePath, file);

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from('shared-files')
    .getPublicUrl(filePath);

  return {
    publicUrl: urlData.publicUrl,
    filePath,
  };
};

// ============================================
// SHARED ITEM CREATION
// ============================================

export interface CreateSharedItemParams {
  title: string;
  description?: string;
  url: string;
  type: 'image' | 'video' | 'document' | 'link' | 'other';
  companyId: string;
  createdById: string;
  entityId?: string;
  entityType?: 'card' | 'requirement';
  tags?: string[];
}

export const createSharedItem = async (params: CreateSharedItemParams) => {
  const {
    title,
    description,
    url,
    type,
    companyId,
    createdById,
    entityId,
    entityType,
    tags,
  } = params;

  const insertData: any = {
    title,
    description,
    url,
    type,
    company_id: companyId,
    created_by_id: createdById,
    tags,
  };

  // Link to entity if provided
  if (entityId && entityType) {
    insertData[entityType === 'card' ? 'card_id' : 'requirement_id'] = entityId;
  }

  const { data, error } = await supabase
    .from('shared_items')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;

  return data;
};

// ============================================
// INLINE MEDIA ITEM CREATION
// ============================================

export const createInlineMediaItem = (
  sharedItemId: string,
  position: number,
  userId: string,
  caption?: string,
): InlineMediaItem => {
  return {
    id: `media-${Date.now()}-${Math.random().toString(36).substring(7)}`,
    sharedItemId,
    position,
    caption,
    insertedAt: new Date().toISOString(),
    insertedBy: userId,
  };
};

// ============================================
// INLINE MEDIA PERSISTENCE
// ============================================

export const saveInlineMediaToDatabase = async (
  entityId: string,
  entityType: 'card' | 'requirement',
  inlineMedia: InlineMediaItem[],
) => {
  const tableName = entityType === 'card' ? 'cards' : 'requirements';

  const { error } = await supabase
    .from(tableName)
    .update({ inline_media: inlineMedia })
    .eq('id', entityId);

  if (error) throw error;
};

// ============================================
// FETCH INLINE MEDIA WITH DETAILS
// ============================================

export interface InlineMediaWithDetails extends InlineMediaItem {
  sharedItem?: {
    id: string;
    title: string;
    description?: string;
    url: string;
    type: string;
    tags?: string[];
    urlMetadata?: any;
    driveFileMetadata?: any;
  };
}

export const fetchInlineMediaWithDetails = async (
  entityId: string,
  entityType: 'card' | 'requirement',
): Promise<InlineMediaWithDetails[]> => {
  // Use the helper function from our migration
  const functionName = entityType === 'card' 
    ? 'get_card_inline_media' 
    : 'get_requirement_inline_media';

  const { data, error } = await supabase.rpc(functionName, {
    [`p_${entityType}_id`]: entityId,
  });

  if (error) throw error;

  return data || [];
};

// ============================================
// EXTRACT INLINE MEDIA IDS FROM HTML
// ============================================

export const extractMediaIdsFromHtml = (html: string): string[] => {
  const mediaIds: string[] = [];
  const regex = /data-media-id="([^"]+)"/g;
  let match;

  while ((match = regex.exec(html)) !== null) {
    mediaIds.push(match[1]);
  }

  return mediaIds;
};

// ============================================
// CLEAN ORPHANED INLINE MEDIA
// ============================================
// Remove inline media items that are no longer in the HTML

export const cleanOrphanedInlineMedia = (
  html: string,
  currentInlineMedia: InlineMediaItem[],
): InlineMediaItem[] => {
  const mediaIdsInHtml = extractMediaIdsFromHtml(html);
  return currentInlineMedia.filter((item) => mediaIdsInHtml.includes(item.id));
};

